<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { ref } from 'vue';

import { usePaxStore } from '@apps/main/new-desktop/core/stores/pax/paxStore';

import { useTripSummaryStore } from '../../stores/tripSummaryStore';
import { useTripOverviewMapStore } from '../../stores/tripOverviewMapStore';
import TripOverviewMap from '../TripOverviewMap.vue';

import TripPriceSummary from './TripPriceSummary.vue';
import TripPriceSummaryFooter from './TripPriceSummaryFooter.vue';

interface TripSummaryProps {
  shouldShowMap: boolean;
  isMainSummary: boolean;
}

const props = defineProps<TripSummaryProps>();

const tripSummaryStore = useTripSummaryStore();
const paxStore = usePaxStore();
const tripOverviewMapStore = useTripOverviewMapStore();

const tripSummaryRef = ref(null);

if (props.isMainSummary) {
  useIntersectionObserver(
    tripSummaryRef,
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]) {
        tripSummaryStore.isTripSummaryVisible = entries[0].isIntersecting;
      }
    }
  );
}
</script>

<template>
  <div ref="tripSummaryRef" class="trip-summary">
    <div class="trip-summary-body">
      <TripOverviewMap
        v-if="
          !tripOverviewMapStore.shouldShowFullScreenMap && props.shouldShowMap
        "
        class="trip-summary-map"
        @zoom="tripOverviewMapStore.openFullScreenDynamicTripMap()"
      />
      <TripPriceSummary
        class="trip-summary-price-summary"
        :class="{ 'without-map': !props.shouldShowMap }"
        :price-segments="tripSummaryStore.tripPriceSegments"
      />
    </div>

    <TripPriceSummaryFooter
      :paxes="paxStore.getPaxesButtonSimpleText"
      :travel-dates="tripSummaryStore.travelDateRange"
      :total-price-with-addons="tripSummaryStore.tripTotalPrice"
      :should-show-share-button="props.isMainSummary"
    />
  </div>
</template>

<style scoped lang="scss">
.trip-summary {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  .trip-summary-body {
    overflow-x: scroll;

    .trip-summary-map {
      height: 225px;
    }

    @include for-less-or-equal-than-extra-small-max {
      overflow-x: auto;
    }

    @include for-less-or-equal-than-medium-max {
      overflow-x: auto;
    }
  }

  .trip-summary-price-summary:not(.without-map) {
    margin-top: var(--spacing-16);
  }
}
</style>
